import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Navbar } from '@/components/layout/Navbar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Progress } from '@/components/ui/progress';
import { useFarmStore } from '@/store/useFarmStore';
import { toast } from 'sonner';
import { ShieldCheck, CreditCard, Banknote, Loader2, AlertTriangle, RotateCcw, Truck, Info, Calculator, Smartphone } from 'lucide-react';
import { api } from '@/lib/api-client';
import { Order, CartItem, PriorityLevel } from '@shared/types';
import { useCurrency } from '@/hooks/use-currency';

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || 'pk_test_placeholder');

const SUPPLIER_DISCOUNT = 0.75;
const MARKUP_RATE = 1.25;

const CITY_FEES: Record<string, number> = {
  accra: 15, kumasi: 25, takoradi: 35, tamale: 40,
  lagos: 20, kano: 35, ibadan: 25, port_harcourt: 30
};

interface CartItemWithToggle extends CartItem {
  enabled: boolean;
}

function inferPriority(item: CartItem): PriorityLevel {
  const subcategory = item.product.subcategory;
  if (subcategory === 'animal') return 'essential';
  if (subcategory === 'feed') return 'recommended';
  return 'optional';
}

function calculateSuccessProbability(items: CartItemWithToggle[], enabledItems: CartItemWithToggle[]): number {
  const essentialItems = items.filter(i => inferPriority(i) === 'essential');
  const enabledEssential = enabledItems.filter(i => inferPriority(i) === 'essential');
  const enabledRecommended = enabledItems.filter(i => inferPriority(i) === 'recommended');
  
  if (essentialItems.length === 0) return 50;
  
  const essentialRatio = enabledEssential.length / essentialItems.length;
  const recommendedRatio = enabledRecommended.length > 0 ? enabledRecommended.length / (items.filter(i => inferPriority(i) === 'recommended').length || 1) : 0.5;
  
  const baseScore = 50;
  const essentialBonus = essentialRatio * 35;
  const recommendedBonusCalc = recommendedRatio * 15;
  
  return Math.min(95, Math.max(20, Math.round(baseScore + essentialBonus + recommendedBonusCalc)));
}

function calculateDeliveryFee(city: string, items: CartItemWithToggle[], enabledItems: CartItemWithToggle[]): { base: number; weightFee: number; distanceFee: number; total: number } {
  const cityKey = city.toLowerCase().replace(/\s+/g, '_');
  const baseFee = CITY_FEES[cityKey] || CITY_FEES[city.toLowerCase()] || 35;
  
  const distanceZones: Record<string, number> = {
    accra: 0, lagos: 0, kumasi: 10, abeokuta: 10,
    tema: 5, oshodi: 5, ikeja: 5, ibadan: 15,
    takoradi: 25, tamale: 30, kano: 40, port_harcourt: 20
  };
  const cityLower = city.toLowerCase();
  const distanceFee = Object.entries(distanceZones).find(([key]) => cityLower.includes(key))?.[1] || 20;
  
  return {
    base: baseFee,
    weightFee: 0,
    distanceFee,
    total: baseFee + distanceFee
  };
}

interface CheckoutFormProps {
  total: number;
  formatFromBase: (amount: number) => string;
  onSuccess: () => void;
  onCancel: () => void;
  isProcessing: boolean;
}

function CheckoutForm({ total, formatFromBase, onSuccess, onCancel, isProcessing }: CheckoutFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!stripe || !elements) {
      return;
    }

    setLoading(true);
    setError(null);

    const { error: submitError } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: window.location.origin + '/order-success',
      },
      redirect: 'if_required',
    });

    if (submitError) {
      setError(submitError.message || 'Payment failed. Please try again.');
      setLoading(false);
      return;
    }

    onSuccess();
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <PaymentElement />
      
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}
      
      <div className="flex flex-col gap-3">
        <Button 
          type="submit" 
          className="w-full h-11 rounded-md"
          disabled={!stripe || loading || isProcessing}
        >
          {(loading || isProcessing) ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Processing...
            </>
          ) : (
            `Pay ${formatFromBase(total)}`
          )}
        </Button>
        
        <Button 
          type="button" 
          variant="outline"
          className="w-full rounded-md"
          onClick={onCancel}
          disabled={loading || isProcessing}
        >
          Cancel
        </Button>
      </div>
      
      <div className="flex items-center justify-center gap-2 text-[10px] text-muted-foreground uppercase tracking-widest bg-muted/30 p-2 rounded">
        <ShieldCheck className="h-3 w-3" /> SSL Secured Payment
      </div>
    </form>
  );
}

export function CheckoutPage() {
  const cart = useFarmStore((s) => s.cart);
  const clearCart = useFarmStore((s) => s.clearCart);
  const activePromo = useFarmStore((s) => s.activePromo);
  const recommendations = useFarmStore((s) => s.recommendations);
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { countryCode, formatFromBase, countries } = useCurrency();
  const defaultCountry = countries.find((country) => country.code === countryCode)?.name || countries[0]?.name || 'Ghana';
  
  const [cartItems, setCartItems] = useState<CartItemWithToggle[]>([]);
  const [showSupplierPrices, setShowSupplierPrices] = useState(false);
  const [selectedCity, setSelectedCity] = useState('Accra');
  const [essentialWarning, setEssentialWarning] = useState<{ item: CartItemWithToggle; open: boolean }>({ item: {} as CartItemWithToggle, open: false });
  const [originalCart, setOriginalCart] = useState<CartItem[]>([]);
  const [createdOrder, setCreatedOrder] = useState<Order | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);

  useEffect(() => {
    const itemsWithToggle: CartItemWithToggle[] = cart.map(item => ({
      ...item,
      enabled: true
    }));
    setCartItems(itemsWithToggle);
    if (originalCart.length === 0 && cart.length > 0) {
      setOriginalCart([...cart]);
    }
  }, [cart, originalCart.length]);

  const enabledItems = useMemo(() => cartItems.filter(i => i.enabled), [cartItems]);

  const subtotal = useMemo(() => {
    return enabledItems.reduce((acc, item) => {
      const itemTotal = item.frequency === 'monthly' ? item.subtotal * 0.9 : item.subtotal;
      return acc + itemTotal;
    }, 0);
  }, [enabledItems]);

  const discount = activePromo
    ? (activePromo.discountType === 'percentage' ? (subtotal * activePromo.value) / 100 : activePromo.value)
    : 0;

  const supplierSubtotal = useMemo(() => {
    return enabledItems.reduce((acc, item) => {
      const supplierPrice = item.product.price * SUPPLIER_DISCOUNT;
      const itemTotal = item.frequency === 'monthly' ? supplierPrice * item.quantity * 0.9 : supplierPrice * item.quantity;
      return acc + itemTotal;
    }, 0);
  }, [enabledItems]);

  const markup = subtotal - supplierSubtotal;

  const deliveryFee = useMemo(() => calculateDeliveryFee(selectedCity, cartItems, enabledItems), [selectedCity, cartItems, enabledItems]);

  const total = Math.max(0, subtotal + deliveryFee.total - discount);

  const successProbability = useMemo(() => calculateSuccessProbability(cartItems, enabledItems), [cartItems, enabledItems]);

  const financingOptions = useMemo(() => {
    if (total <= 5000) return null;
    return {
      payIn2: { first: Math.round(total / 2), second: Math.round(total / 2) },
      payIn3: { first: Math.round(total * 0.4), second: Math.round(total * 0.3), third: Math.round(total * 0.3) }
    };
  }, [total]);

  const handleToggleItem = (itemId: string) => {
    const item = cartItems.find(i => i.product.id === itemId);
    if (!item) return;
    
    const priority = inferPriority(item);
    if (priority === 'essential' && !item.enabled) {
      setEssentialWarning({ item, open: true });
      return;
    }
    
    setCartItems(prev => prev.map(i => 
      i.product.id === itemId ? { ...i, enabled: !i.enabled } : i
    ));
  };

  const handleRemoveEssential = () => {
    setCartItems(prev => prev.map(i => 
      i.product.id === essentialWarning.item.product.id ? { ...i, enabled: false } : i
    ));
    setEssentialWarning({ item: {} as CartItemWithToggle, open: false });
    toast.warning('Essential item removed. This may affect your farm success probability.');
  };

  const handleRestoreAI = () => {
    if (originalCart.length === 0) {
      toast.error('No original AI recommendation found.');
      return;
    }
    const restoredItems: CartItemWithToggle[] = originalCart.map(item => ({
      ...item,
      enabled: true
    }));
    setCartItems(restoredItems);
    toast.success('AI recommendation restored.');
  };

  const handlePlaceOrder = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    const formData = new FormData(e.currentTarget);
    const shippingDetails = {
      fullname: formData.get('fullname') as string,
      phone: formData.get('phone') as string,
      address: formData.get('address') as string,
      city: formData.get('city') as string,
      country: formData.get('country') as string,
    };

    const paymentMethod = formData.get('paymentMethod') as string;
    
    const orderPayload = {
      items: enabledItems.map(item => ({ 
        productId: item.product.id, 
        quantity: item.quantity,
        frequency: item.frequency 
      })),
      totalPrice: Math.round(total * 100),
      shippingDetails,
      promoCode: activePromo?.code,
    };
    
    try {
      const order = await api<Order>('/api/checkout/create-order', {
        method: 'POST',
        body: JSON.stringify(orderPayload),
      });
      
      setCreatedOrder(order);
      
      const currency = shippingDetails.country?.toLowerCase().includes('nigeria') ? 'NGN' : 
                       shippingDetails.country?.toLowerCase().includes('ghana') ? 'GHS' : 'GHS';
      
      const paymentIntentResult = await api<{ clientSecret: string; paymentIntentId: string }>('/api/checkout/payment-intent', {
        method: 'POST',
        body: JSON.stringify({
          orderId: order.id,
          amount: Math.round(total * 100),
          currency,
          paymentMethod: paymentMethod === 'card' ? 'card' : 'mobile_money',
        }),
      });
      
      setClientSecret(paymentIntentResult.clientSecret);
      setShowPaymentForm(true);
      toast.success('Order created. Please complete payment.');
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Failed to create order. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePaymentSuccess = async () => {
    if (!createdOrder || !clientSecret) return;
    
    setIsProcessingPayment(true);
    try {
      const paymentIntentId = clientSecret.split('_secret_')[0];
      
      await api('/api/checkout/complete', {
        method: 'POST',
        body: JSON.stringify({
          orderId: createdOrder.id,
          paymentIntentId,
        }),
      });
      
      toast.success('Payment successful! Order confirmed.');
      clearCart();
      navigate(`/order-success/${createdOrder.id}`);
    } catch (error) {
      console.error('Payment completion error:', error);
      toast.error('Payment completed but order confirmation failed. Please contact support.');
    } finally {
      setIsProcessingPayment(false);
    }
  };

  const handleCancelPayment = () => {
    setShowPaymentForm(false);
    setClientSecret(null);
    setCreatedOrder(null);
    toast.info('Payment cancelled. You can try again.');
  };

  if (cart.length === 0) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 py-20 text-center space-y-4">
          <h2 className="text-xl font-bold">Your cart is empty.</h2>
          <Button onClick={() => navigate('/catalog')}>Return to Catalog</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/10">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 py-8 md:py-12">
        <form onSubmit={handlePlaceOrder} className="grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-8 space-y-6">
            <Card className="rounded-md border shadow-sm bg-background">
              <CardHeader>
                <CardTitle className="text-lg font-bold">Delivery Information</CardTitle>
              </CardHeader>
              <CardContent className="grid gap-6">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="fullname" className="text-xs uppercase font-bold text-muted-foreground">Full Name</Label>
                    <Input id="fullname" name="fullname" placeholder="John Doe" required />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-xs uppercase font-bold text-muted-foreground">Phone Number</Label>
                    <Input id="phone" name="phone" placeholder="+234..." required />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="address" className="text-xs uppercase font-bold text-muted-foreground">Delivery Address</Label>
                  <Input id="address" name="address" placeholder="123 Farm Lane" required />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs uppercase font-bold text-muted-foreground">Country</Label>
                    <Select name="country" defaultValue={defaultCountry}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select Country" />
                      </SelectTrigger>
                      <SelectContent>
                        {countries.map((country) => (
                          <SelectItem key={country.code} value={country.name}>
                            {country.flag} {country.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="city" className="text-xs uppercase font-bold text-muted-foreground">City / Region</Label>
                    <Select name="city" value={selectedCity} onValueChange={setSelectedCity}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select City" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Accra">Accra (GH) - ₵15</SelectItem>
                        <SelectItem value="Kumasi">Kumasi (GH) - ₵25</SelectItem>
                        <SelectItem value="Takoradi">Takoradi (GH) - ₵35</SelectItem>
                        <SelectItem value="Tamale">Tamale (GH) - ₵40</SelectItem>
                        <SelectItem value="Lagos">Lagos (NG) - ₵20</SelectItem>
                        <SelectItem value="Kano">Kano (NG) - ₵35</SelectItem>
                        <SelectItem value="Ibadan">Ibadan (NG) - ₵25</SelectItem>
                        <SelectItem value="Port Harcourt">Port Harcourt (NG) - ₵30</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="rounded-md border shadow-sm bg-background">
              <CardHeader>
                <CardTitle className="text-lg font-bold">Package Customization</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between pb-4 border-b">
                  <div className="flex items-center gap-2">
                    <div className="flex flex-col">
                      <span className="text-sm font-semibold">Success Probability</span>
                      <span className="text-xs text-muted-foreground">Based on your current selection</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Progress value={successProbability} className="w-24 h-2" />
                    <span className={`text-lg font-bold ${
                      successProbability >= 80 ? 'text-green-600' :
                      successProbability >= 60 ? 'text-yellow-600' : 'text-red-600'
                    }`}>
                      {successProbability}%
                    </span>
                  </div>
                </div>

                {originalCart.length > 0 && (
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm" 
                    onClick={handleRestoreAI}
                    className="w-full mb-4"
                  >
                    <RotateCcw className="h-4 w-4 mr-2" />
                    Restore AI Recommendation
                  </Button>
                )}

                <div className="space-y-3">
                  <Label className="text-xs uppercase font-bold text-muted-foreground">Items in Your Package</Label>
                  {cartItems.map((item) => {
                    const priority = inferPriority(item);
                    return (
                      <div 
                        key={`${item.product.id}-${item.frequency}`} 
                        className={`flex items-center justify-between p-3 rounded-md border ${
                          item.enabled ? 'bg-background' : 'bg-muted/30 opacity-60'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <Switch
                            checked={item.enabled}
                            onCheckedChange={() => handleToggleItem(item.product.id)}
                          />
                          <div className="flex flex-col">
                            <span className="text-sm">{item.quantity}x {item.product.name}</span>
                            <span className={`text-xs ${
                              priority === 'essential' ? 'text-red-600 font-medium' :
                              priority === 'recommended' ? 'text-blue-600' : 'text-muted-foreground'
                            }`}>
                              {priority === 'essential' && '⚠️ '}
                              {priority.charAt(0).toUpperCase() + priority.slice(1)}
                              {item.frequency === 'monthly' && ' (Monthly -10%)'}
                            </span>
                          </div>
                        </div>
                        <span className="text-sm font-bold">
                          {formatFromBase(item.frequency === 'monthly' ? item.subtotal * 0.9 : item.subtotal)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            <Card className="rounded-md border shadow-sm bg-background">
              <CardHeader>
                <CardTitle className="text-lg font-bold">Payment Method</CardTitle>
              </CardHeader>
              <CardContent>
                <RadioGroup name="paymentMethod" defaultValue="card" className="grid gap-3">
                  <Label
                    htmlFor="card"
                    className="flex items-center justify-between p-4 border rounded-md cursor-pointer hover:bg-muted/50 transition-colors [&:has([data-state=checked])]:border-primary [&:has([data-state=checked])]:bg-primary/5"
                  >
                    <div className="flex items-center gap-3">
                      <RadioGroupItem value="card" id="card" />
                      <div className="flex flex-col">
                        <span className="text-sm font-bold">Debit/Credit Card</span>
                        <span className="text-xs text-muted-foreground">Secure payment via Stripe</span>
                      </div>
                    </div>
                    <CreditCard className="h-4 w-4 text-muted-foreground" />
                  </Label>
                  <Label
                    htmlFor="mobile_money"
                    className="flex items-center justify-between p-4 border rounded-md cursor-pointer hover:bg-muted/50 transition-colors [&:has([data-state=checked])]:border-primary [&:has([data-state=checked])]:bg-primary/5"
                  >
                    <div className="flex items-center gap-3">
                      <RadioGroupItem value="mobile_money" id="mobile_money" />
                      <div className="flex flex-col">
                        <span className="text-sm font-bold">Mobile Money</span>
                        <span className="text-xs text-muted-foreground">MTN, Vodafone, AirtelTigo</span>
                      </div>
                    </div>
                    <Smartphone className="h-4 w-4 text-muted-foreground" />
                  </Label>
                </RadioGroup>

                {financingOptions && (
                  <div className="mt-4 pt-4 border-t">
                    <Label className="text-xs uppercase font-bold text-muted-foreground mb-3 block">Financing Options</Label>
                    <div className="grid sm:grid-cols-2 gap-3">
                      <Label
                        htmlFor="installment-2"
                        className="flex items-center justify-between p-3 border rounded-md cursor-pointer hover:bg-muted/50 transition-colors [&:has([data-state=checked])]:border-primary [&:has([data-state=checked])]:bg-primary/5"
                      >
                        <div className="flex flex-col">
                          <span className="text-sm font-bold">Pay in 2 Installments</span>
                          <span className="text-xs text-green-600">
                            {formatFromBase(financingOptions.payIn2.first)} now + {formatFromBase(financingOptions.payIn2.second)} in 30 days
                          </span>
                        </div>
                        <RadioGroupItem value="installment-2" id="installment-2" />
                      </Label>
                      <Label
                        htmlFor="installment-3"
                        className="flex items-center justify-between p-3 border rounded-md cursor-pointer hover:bg-muted/50 transition-colors [&:has([data-state=checked])]:border-primary [&:has([data-state=checked])]:bg-primary/5"
                      >
                        <div className="flex flex-col">
                          <span className="text-sm font-bold">Pay in 3 Installments</span>
                          <span className="text-xs text-green-600">
                            {formatFromBase(financingOptions.payIn3.first)} + 2 x {formatFromBase(financingOptions.payIn3.second)}
                          </span>
                        </div>
                        <RadioGroupItem value="installment-3" id="installment-3" />
                      </Label>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {showPaymentForm && clientSecret && (
              <Card className="rounded-md border shadow-sm bg-background">
                <CardHeader>
                  <CardTitle className="text-lg font-bold">Complete Payment</CardTitle>
                </CardHeader>
                <CardContent>
                  <Elements stripe={stripePromise} options={{ clientSecret }}>
                    <CheckoutForm 
                      total={total} 
                      formatFromBase={formatFromBase}
                      onSuccess={handlePaymentSuccess}
                      onCancel={handleCancelPayment}
                      isProcessing={isProcessingPayment}
                    />
                  </Elements>
                </CardContent>
              </Card>
            )}
          </div>

          <div className="lg:col-span-4 space-y-6">
            <Card className="rounded-md border shadow-sm bg-background sticky top-24">
              <CardHeader>
                <CardTitle className="text-lg font-bold">Order Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  {cartItems.map((item) => (
                    <div 
                      key={`${item.product.id}-${item.frequency}`} 
                      className={`flex justify-between text-xs ${!item.enabled ? 'line-through text-muted-foreground' : ''}`}
                    >
                      <div className="flex-1">
                        <div className="text-foreground line-clamp-1">
                          {item.enabled ? '' : '❌ '}
                          {item.quantity}x {item.product.name}
                        </div>
                        {item.frequency === 'monthly' && item.enabled && (
                          <div className="text-[10px] text-primary font-bold">Monthly Plan (10% Off)</div>
                        )}
                      </div>
                      {item.enabled && (
                        <span className="font-bold ml-4">
                          {formatFromBase(item.frequency === 'monthly' ? item.subtotal * 0.9 : item.subtotal)}
                        </span>
                      )}
                    </div>
                  ))}
                </div>

                <div className="pt-4 border-t space-y-2">
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>Subtotal</span>
                    <span>{formatFromBase(subtotal)}</span>
                  </div>

                  {showSupplierPrices && (
                    <>
                      <div className="flex justify-between text-xs text-muted-foreground">
                        <span>Supplier Cost</span>
                        <span>{formatFromBase(supplierSubtotal)}</span>
                      </div>
                      <div className="flex justify-between text-xs text-amber-600">
                        <span>Our Markup (25%)</span>
                        <span>{formatFromBase(markup)}</span>
                      </div>
                    </>
                  )}

                  <div className="flex justify-between text-xs text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <Truck className="h-3 w-3" />
                      <span>Delivery Fee</span>
                    </div>
                    <span>{formatFromBase(deliveryFee.total)}</span>
                  </div>

                  {deliveryFee.distanceFee > 0 && (
                    <div className="text-[10px] text-muted-foreground pl-4 space-y-1">
                      <div className="flex justify-between">
                        <span>Base fee ({selectedCity}):</span>
                        <span>{formatFromBase(deliveryFee.base)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Distance surcharge:</span>
                        <span>{formatFromBase(deliveryFee.distanceFee)}</span>
                      </div>
                    </div>
                  )}

                  {discount > 0 && (
                    <div className="flex justify-between text-xs text-green-600 font-bold">
                      <span>Discount</span>
                      <span>-{formatFromBase(discount)}</span>
                    </div>
                  )}

                  <div className="flex justify-between text-base font-bold pt-2">
                    <span>Total</span>
                    <span className="text-primary">{formatFromBase(total)}</span>
                  </div>
                </div>

                <div className="flex items-center justify-between pt-2 border-t">
                  <div className="flex items-center gap-2">
                    <Switch
                      id="show-supplier"
                      checked={showSupplierPrices}
                      onCheckedChange={setShowSupplierPrices}
                    />
                    <Label htmlFor="show-supplier" className="text-xs text-muted-foreground cursor-pointer">
                      Show supplier pricing
                    </Label>
                  </div>
                  {showSupplierPrices && (
                    <div className="flex items-center gap-1 text-[10px] text-amber-600">
                      <Info className="h-3 w-3" />
                      <span>75% of our price</span>
                    </div>
                  )}
                </div>

                <Button type="submit" className="w-full h-11 rounded-md" disabled={isSubmitting}>
                  {isSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  {isSubmitting ? 'Confirming...' : 'Place Order'}
                </Button>
                <div className="flex items-center justify-center gap-2 text-[10px] text-muted-foreground uppercase tracking-widest bg-muted/30 p-2 rounded">
                  <ShieldCheck className="h-3 w-3" /> SSL Secured Payment
                </div>
              </CardContent>
            </Card>
          </div>
        </form>
      </div>

      <AlertDialog open={essentialWarning.open} onOpenChange={(open) => setEssentialWarning(prev => ({ ...prev, open }))}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-yellow-500" />
              Remove Essential Item?
            </AlertDialogTitle>
            <AlertDialogDescription>
              You are about to remove "{essentialWarning.item.product?.name}" which is marked as essential for your farm setup.
              Removing this item may significantly reduce your chances of success. Are you sure you want to continue?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setEssentialWarning({ item: {} as CartItemWithToggle, open: false })}>
              Keep Item
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleRemoveEssential} className="bg-red-600 hover:bg-red-700">
              Remove Anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}